import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean
  // When true, skip automatic 401 handling (refresh / forced logout)
  skipAuthHandling?: boolean
  _retry?: boolean
}

const CLASSIFY_EMBED_ACCESS_TOKEN_HEADER = 'X-Classify-Embed-Access-Token'

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json; charset=utf-8'
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: CustomAxiosRequestConfig): CustomAxiosRequestConfig => {
    if (!config.skipAuth && !config.headers.Authorization) {
      const token = localStorage.getItem('id_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  },
  (error: AxiosError): Promise<never> => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<AxiosResponse | never> => {
    const originalRequest = error.config as CustomAxiosRequestConfig

    // Allow certain requests (e.g. explicit logout) to bypass auto 401 handling
    if (originalRequest?.skipAuthHandling || originalRequest?.skipAuth) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      // Don't attempt refresh for the refresh endpoint itself
      if (!originalRequest.url?.includes('refresh')) {
        // Try to refresh the token once and then retry the original request
        try {
          const { useAuthStore } = await import('@/stores/auth')
          const authStore = useAuthStore()
          await authStore.refreshToken()

          // Retry original request with new token
          const token = localStorage.getItem('id_token')
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          const accessToken = localStorage.getItem('access_token')
          if (accessToken && originalRequest.headers?.[CLASSIFY_EMBED_ACCESS_TOKEN_HEADER]) {
            originalRequest.headers[CLASSIFY_EMBED_ACCESS_TOKEN_HEADER] = accessToken
          }
          return api(originalRequest)
        } catch (refreshError) {
          // Refresh failed, redirect to login
          const { useAuthStore } = await import('@/stores/auth')
          const authStore = useAuthStore()
          authStore.logout('Session expired')
          return Promise.reject(refreshError)
        }
      }
    }

    return Promise.reject(error)
  }
)

export default api
